import { useState } from 'react';
import { Autocomplete } from '@react-google-maps/api';

// Hooks
import useGoogleAutocomplete from '../../../hooks/useGoogleAutocomplete';

/**
 * @typedef AddressAutocompleteProps
 * @property {function(google.maps.places.Place): void} onChange - Function to call when the place changes.
 * @property {function(string): void} onChangeInput - Function to call when the input changes.
 * @property {function(): void} trigger - Function to trigger the form validation.
 * @property {function(function(React.ChangeEvent<HTMLInputElement>): void, function(): void): JSX.Element} children - The children of the component.
 */

/**
 * AddressAutocomplete - Component for address autocomplete with Google Places, limited to France.
 * @param {AddressAutocompleteProps} props - The properties of the component
 * @returns {JSX.Element}
 */
function AddressAutocomplete({
  onChange, onChangeInput, trigger, children,
}) {
  const {
    isLoaded,
    onLoadAutocomplete,
    autocomplete,
  } = useGoogleAutocomplete({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY,
  });

  const [activePlace, setActivePlace] = useState(null);

  /**
   * handlePlaceChanged - Function to handle the place changed event.
   * @returns {void}
   */
  const handlePlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      setActivePlace(place);
      onChange(place);

      const address = place.formatted_address || '';
      onChangeInput(address);
      trigger();
    }
  };

  /**
   * handleBlur - Function to handle the blur event.
   * @returns {void}
   */
  const handleBlur = () => {
    if (!activePlace) {
      onChange({});
      trigger();
    }
  };

  /**
   * handleBlur - Function to handle the blur event.
   * @param {React.FocusEvent<HTMLInputElement>} e - The focus event object.
   * @returns {void}
   */
  const handleOnChange = (e) => {
    setActivePlace(null);
    onChangeInput(e.target.value || '');
  };

  if (!isLoaded) {
    return (
      <>
        {children(handleOnChange, handleBlur)}
      </>
    );
  }

  return (
    <div>
      <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={handlePlaceChanged}>
        {children(handleOnChange, handleBlur)}
      </Autocomplete>
    </div>
  );
}

export default AddressAutocomplete;
